/**
 * Client-side CalNet packet decoding.
 *
 * CalNet packet format (after bridge header):
 *   [nonce:12][ciphertext][tag:16]
 *
 * Discovery packets use zero nonce and are cleartext.
 * All other packets are AES-256-GCM encrypted with the net_key.
 */

import * as flatbuffers from 'flatbuffers';
import {
  Envelope,
  PayloadType,
  BullyMessage,
  BullyUnion,
  EntityMessage,
  EntityUnion,
} from '@caldera/protocol';

const CALNET_NONCE_LENGTH = 12;
const CALNET_TAG_LENGTH = 16;

// Cache for imported CryptoKeys (hex -> CryptoKey)
const keyCache = new Map<string, CryptoKey>();

/**
 * Import a hex-encoded net_key as a CryptoKey for AES-GCM decryption.
 */
async function importNetKey(netKeyHex: string): Promise<CryptoKey | null> {
  // Check cache first
  const cached = keyCache.get(netKeyHex);
  if (cached) return cached;

  try {
    // Convert hex to bytes
    const keyBytes = new Uint8Array(
      netKeyHex.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16)) || []
    );

    if (keyBytes.length !== 32) {
      return null;
    }

    const key = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    );

    keyCache.set(netKeyHex, key);
    return key;
  } catch {
    return null;
  }
}

/**
 * Decrypt AES-GCM encrypted data with AAD.
 * Returns decrypted bytes or null if decryption fails.
 */
async function decryptAesGcm(
  ciphertext: Uint8Array,
  tag: Uint8Array,
  nonce: Uint8Array,
  aad: Uint8Array,
  key: CryptoKey
): Promise<Uint8Array | null> {
  try {
    // Web Crypto expects ciphertext + tag concatenated
    const ciphertextWithTag = new Uint8Array(ciphertext.length + tag.length);
    ciphertextWithTag.set(ciphertext, 0);
    ciphertextWithTag.set(tag, ciphertext.length);

    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: new Uint8Array(nonce).buffer,
        additionalData: new Uint8Array(aad).buffer,
        tagLength: 128, // 16 bytes * 8 bits
      },
      key,
      ciphertextWithTag
    );
    return new Uint8Array(decrypted);
  } catch {
    // Decryption failed - wrong key or corrupted data
    return null;
  }
}

export interface DecodedPacket {
  payloadType: string;
  payloadTypeNum: number;
  subType?: string;
  isDiscovery?: boolean;
  error?: string;
}

// Map PayloadType enum to readable names
const PAYLOAD_TYPE_NAMES: Record<number, string> = {
  [PayloadType.None]: 'None',
  [PayloadType.DiscoveryRequest]: 'Discovery',
  [PayloadType.DiscoveryResponse]: 'DiscoveryResp',
  [PayloadType.Command]: 'Command',
  [PayloadType.Bully]: 'Bully',
  [PayloadType.ProvisionRequest]: 'Provision',
  [PayloadType.ProvisionResponse]: 'ProvisionResp',
  [PayloadType.PingRequest]: 'Ping',
  [PayloadType.PingResponse]: 'Pong',
  [PayloadType.Signal]: 'Signal',
  [PayloadType.Entity]: 'Entity',
  [PayloadType.System]: 'System',
  [PayloadType.Debug]: 'Debug',
  [PayloadType.IdentifyRequest]: 'Identify',
  [PayloadType.IdentifyResponse]: 'IdentifyResp',
};

// Map BullyUnion to readable names
const BULLY_TYPE_NAMES: Record<number, string> = {
  [BullyUnion.NONE]: '',
  [BullyUnion.BullyAlive]: 'Alive',
  [BullyUnion.BullyElection]: 'Election',
  [BullyUnion.BullyAnswer]: 'Answer',
  [BullyUnion.BullyCoordinator]: 'Coordinator',
  [BullyUnion.BullyGetConfig]: 'GetConfig',
  [BullyUnion.BullyGetConfigResponse]: 'ConfigResp',
  [BullyUnion.BullySetConfig]: 'SetConfig',
  [BullyUnion.BullySetConfigResponse]: 'SetConfigResp',
  [BullyUnion.BullyGetDeviceState]: 'GetState',
  [BullyUnion.BullyGetDeviceStateResponse]: 'StateResp',
};

// Map EntityUnion to readable names
const ENTITY_TYPE_NAMES: Record<number, string> = {
  [EntityUnion.NONE]: '',
  [EntityUnion.SetEntityState]: 'Set',
  [EntityUnion.GetEntityState]: 'Get',
  [EntityUnion.EntityStateResponse]: 'StateResp',
  [EntityUnion.ActivateScene]: 'Scene',
  [EntityUnion.GetEntityConfiguration]: 'GetConfig',
  [EntityUnion.GetEntityConfigurationResponse]: 'ConfigResp',
  [EntityUnion.GetEntityModes]: 'GetModes',
  [EntityUnion.GetEntityModesResponse]: 'ModesResp',
};

/**
 * Decode CalNet packet from base64 payload preview.
 * If netKeyHex is provided, attempts to decrypt encrypted packets.
 */
export async function decodePacket(
  payloadPreviewB64: string,
  netKeyHex?: string
): Promise<DecodedPacket | null> {
  try {
    // Decode base64 to bytes
    const bytes = Uint8Array.from(atob(payloadPreviewB64), (c) => c.charCodeAt(0));

    // We receive full CalNet payload (after bridge header)
    // Format: [nonce:12][ciphertext][tag:16]

    if (bytes.length < CALNET_NONCE_LENGTH + 8) {
      return { payloadType: 'short', payloadTypeNum: -1 };
    }

    // Check if nonce is all zeros (cleartext Discovery) or real (encrypted)
    const nonceBytes = bytes.slice(0, CALNET_NONCE_LENGTH);
    const isZeroNonce = nonceBytes.every((b) => b === 0);

    let envelopeBytes: Uint8Array;

    // If nonce is non-zero, packet has encrypted payload
    if (!isZeroNonce) {
      // CalNet format: [nonce:12][Envelope FlatBuffer][tag:16]
      // The Envelope header is cleartext (AAD), only the payload bytes inside are encrypted

      // Extract tag (last 16 bytes)
      const tag = bytes.slice(bytes.length - CALNET_TAG_LENGTH);
      // Envelope is between nonce and tag
      const envelopeWithCiphertext = bytes.slice(CALNET_NONCE_LENGTH, bytes.length - CALNET_TAG_LENGTH);

      // Parse envelope to find payload location (even though payload is encrypted, the structure is readable)
      const buf = new flatbuffers.ByteBuffer(envelopeWithCiphertext);
      const envelope = Envelope.getRootAsEnvelope(buf);
      const payloadTypeNum = envelope.payloadType();

      // If no key provided, we can still return the payload type from the cleartext header
      if (!netKeyHex) {
        const payloadType = PAYLOAD_TYPE_NAMES[payloadTypeNum] || `Type${payloadTypeNum}`;
        return { payloadType, payloadTypeNum };
      }

      // Try to decrypt the payload to get subtypes
      const key = await importNetKey(netKeyHex);
      if (!key) {
        const payloadType = PAYLOAD_TYPE_NAMES[payloadTypeNum] || `Type${payloadTypeNum}`;
        return { payloadType, payloadTypeNum, error: 'invalid key' };
      }

      // Find the payload vector location in the FlatBuffer
      const payloadArray = envelope.payloadArray();
      if (!payloadArray || payloadArray.length === 0) {
        const payloadType = PAYLOAD_TYPE_NAMES[payloadTypeNum] || `Type${payloadTypeNum}`;
        return { payloadType, payloadTypeNum };
      }

      // Calculate AAD: everything from envelope start up to the payload data
      const payloadOffset = payloadArray.byteOffset;
      if (payloadOffset < 0 || payloadOffset >= envelopeWithCiphertext.length) {
        const payloadType = PAYLOAD_TYPE_NAMES[payloadTypeNum] || `Type${payloadTypeNum}`;
        return { payloadType, payloadTypeNum, error: 'bad offset' };
      }

      const aad = envelopeWithCiphertext.slice(0, payloadOffset);
      const ciphertext = envelopeWithCiphertext.slice(payloadOffset, payloadOffset + payloadArray.length);

      const decrypted = await decryptAesGcm(ciphertext, tag, nonceBytes, aad, key);
      if (!decrypted) {
        const payloadType = PAYLOAD_TYPE_NAMES[payloadTypeNum] || `Type${payloadTypeNum}`;
        return { payloadType, payloadTypeNum, error: 'decrypt failed' };
      }

      // Now parse the decrypted payload for subtypes
      const payloadType = PAYLOAD_TYPE_NAMES[payloadTypeNum] || `Type${payloadTypeNum}`;
      const isDiscovery = payloadTypeNum === PayloadType.DiscoveryRequest || payloadTypeNum === PayloadType.DiscoveryResponse;
      const result: DecodedPacket = { payloadType, payloadTypeNum, isDiscovery };

      // Extract subtype from decrypted payload
      if (decrypted.length >= 8) {
        try {
          const payloadBuf = new flatbuffers.ByteBuffer(decrypted);
          if (payloadTypeNum === PayloadType.Bully) {
            const bullyMsg = BullyMessage.getRootAsBullyMessage(payloadBuf);
            const msgType = bullyMsg.messageType();
            const subType = BULLY_TYPE_NAMES[msgType];
            if (subType) result.subType = subType;
          } else if (payloadTypeNum === PayloadType.Entity) {
            const entityMsg = EntityMessage.getRootAsEntityMessage(payloadBuf);
            const msgType = entityMsg.messageType();
            const subType = ENTITY_TYPE_NAMES[msgType];
            if (subType) result.subType = subType;
          }
        } catch {
          // Subtype parsing failed
        }
      }

      return result;
    } else {
      // Cleartext packet (Discovery) - skip nonce
      envelopeBytes = bytes.slice(CALNET_NONCE_LENGTH);
    }

    // Try to parse as FlatBuffer Envelope
    const buf = new flatbuffers.ByteBuffer(envelopeBytes);

    // Validate the root offset is reasonable
    const rootOffset = buf.readInt32(0);
    if (rootOffset < 0 || rootOffset > envelopeBytes.length - 4) {
      return { payloadType: 'invalid', payloadTypeNum: -1, error: 'bad root offset' };
    }

    const envelope = Envelope.getRootAsEnvelope(buf);
    const payloadTypeNum = envelope.payloadType();

    // Validate payload type is in expected range
    if (payloadTypeNum < 0 || payloadTypeNum > 20) {
      return { payloadType: 'invalid', payloadTypeNum, error: `type=${payloadTypeNum}` };
    }

    const payloadType = PAYLOAD_TYPE_NAMES[payloadTypeNum] || `Type${payloadTypeNum}`;
    const isDiscovery =
      payloadTypeNum === PayloadType.DiscoveryRequest ||
      payloadTypeNum === PayloadType.DiscoveryResponse;

    const result: DecodedPacket = {
      payloadType,
      payloadTypeNum,
      isDiscovery,
    };

    // Try to extract subtype for Bully and Entity messages
    // The payload is a nested FlatBuffer (BullyMessage or EntityMessage)
    // whose messageType field should be readable
    // Wrap in try-catch since payloadArray() can fail on truncated buffers
    try {
      const payloadArray = envelope.payloadArray();

      if (payloadArray && payloadArray.length >= 8) {
        const payloadBuf = new flatbuffers.ByteBuffer(payloadArray);

        if (payloadTypeNum === PayloadType.Bully) {
          const bullyMsg = BullyMessage.getRootAsBullyMessage(payloadBuf);
          const msgType = bullyMsg.messageType();
          const subType = BULLY_TYPE_NAMES[msgType];
          if (subType) {
            result.subType = subType;
          }
        } else if (payloadTypeNum === PayloadType.Entity) {
          const entityMsg = EntityMessage.getRootAsEntityMessage(payloadBuf);
          const msgType = entityMsg.messageType();
          const subType = ENTITY_TYPE_NAMES[msgType];
          if (subType) {
            result.subType = subType;
          }
        }
      }
    } catch {
      // Subtype parsing failed (likely truncated buffer), that's ok - we still have the main type
    }

    return result;
  } catch (e) {
    return {
      payloadType: 'error',
      payloadTypeNum: -1,
      error: String(e),
    };
  }
}
